import type { BadgeTone } from "@/components/StatusBadge";
import { isZeroQuantity } from "./format";

/**
 * Pick a badge tone from figures the server sent.
 *
 * This reads the numbers, it never computes them: `available` already came from
 * the backend, which owns stock truth.
 */
export function stockTone(available: string, book: string): BadgeTone {
  if (isZeroQuantity(book)) return "out";
  if (isZeroQuantity(available)) return "reserved";
  return "available";
}

export function stockLabel(available: string, book: string): string {
  if (isZeroQuantity(book)) return "\u00c9puis\u00e9";
  if (isZeroQuantity(available)) return "Tout r\u00e9serv\u00e9";
  return "Disponible";
}

/**
 * The badge for a tool's lifecycle.
 *
 * Reads what the server decided; it does not re-derive availability.
 */
export function assetTone(lifecycle: string): BadgeTone {
  switch (lifecycle) {
    case "IN_SERVICE":
      return "available";
    case "IN_REPAIR":
      return "repair";
    case "OUT_OF_SERVICE":
    case "LOST":
    case "DISCARDED":
    case "SOLD":
      return "out";
    default:
      return "unknown";
  }
}

/**
 * The badge for a loan.
 *
 * Overdue is the only thing that needs to shout: an active loan is normal, a
 * returned one is history, a late one is a phone call to make.
 */
export function loanTone(status: string, isOverdue: boolean): BadgeTone {
  if (status === "RETURNED") return "unknown";
  return isOverdue ? "out" : "loaned";
}

export function projectTone(status: string): BadgeTone {
  switch (status) {
    case "ACTIVE":
      return "available";
    case "PLANNED":
      return "info";
    case "ON_HOLD":
      return "warning";
    case "CANCELLED":
      return "out";
    default:
      return "unknown";
  }
}

/**
 * The badge for one line of a project's list.
 *
 * Only a real shortfall is red. "Partiel" means the stock exists and is free —
 * a click away, not a purchase away — and colouring it like a problem would
 * teach people to ignore the colour that matters.
 */
export function requirementTone(status: string): BadgeTone {
  switch (status) {
    case "OK":
      return "available";
    case "PARTIAL":
      return "reserved";
    case "SHORT":
      return "out";
    default:
      return "unknown";
  }
}

export function purchaseOrderTone(status: string): BadgeTone {
  switch (status) {
    case "RECEIVED":
      return "available";
    case "PARTIALLY_RECEIVED":
      return "reserved";
    case "ORDERED":
      return "info";
    case "CLOSED":
      // Ni « reçu » ni « annulé » : c'est fini, et ce n'est pas complet. Un ton
      // à part, sinon l'oeil range la commande avec celles qui sont arrivées.
      return "warning";
    case "CANCELLED":
      return "out";
    default:
      return "unknown";
  }
}

/** Un surplus de chantier : noté, à décider, entamé, réglé, abandonné. */
export function surplusTone(status: string): BadgeTone {
  switch (status) {
    case "DRAFT":
      return "info";
    case "OPEN":
      // Ce qui attend une décision est ce qui coûte : de la marchandise qui
      // dort sur un chantier sans que personne l'ait décidé.
      return "warning";
    case "PARTIALLY_DISPOSED":
      return "reserved";
    case "RESOLVED":
      return "available";
    case "CANCELLED":
      return "out";
    default:
      return "unknown";
  }
}

/** Une décision : à exécuter, exécutée, annulée. */
export function dispositionTone(status: string): BadgeTone {
  switch (status) {
    case "DRAFT":
      return "warning";
    case "FINALIZED":
      return "available";
    case "CANCELLED":
      return "out";
    default:
      return "unknown";
  }
}

/** Un retour fournisseur : préparé, ou parti. */
export function supplierReturnTone(status: string): BadgeTone {
  switch (status) {
    case "DRAFT":
      return "info";
    case "FINALIZED":
      return "available";
    default:
      return "unknown";
  }
}

/** Une liste : brouillon, en cours, terminée, rangée. */
export function materialListTone(status: string): BadgeTone {
  switch (status) {
    case "ACTIVE":
      return "available";
    case "DRAFT":
      return "info";
    case "COMPLETED":
      return "reserved";
    case "ARCHIVED":
      return "unknown";
    default:
      return "unknown";
  }
}

/** Une ligne de liste. « Annulé » n'est pas une erreur : c'est un choix. */
export function materialListLineTone(status: string): BadgeTone {
  switch (status) {
    case "TO_GET":
      return "warning";
    case "FOUND":
      return "available";
    case "PURCHASED":
      return "info";
    case "CANCELLED":
      return "unknown";
    default:
      return "unknown";
  }
}

/**
 * Une version d'estimation.
 *
 * Figée se lit d'un coup d'oeil : c'est la seule information qui decide si
 * l'ecran laisse modifier quoi que ce soit.
 */
export function estimateVersionTone(status: string): BadgeTone {
  return status === "FINALIZED" ? "reserved" : "info";
}

export function projectEstimateTone(status: string): BadgeTone {
  switch (status) {
    case "ACCEPTED":
      return "available";
    case "ACTIVE":
      return "info";
    case "DRAFT":
      return "unknown";
    case "REJECTED":
      return "out";
    case "ARCHIVED":
      return "unknown";
    default:
      return "unknown";
  }
}

/** Un besoin confirmé : ce qui compte, ce qui attend, ce qui est écarté. */
export function requirementStateTone(state: string): BadgeTone {
  switch (state) {
    case "CONFIRMED":
      return "available";
    case "NEEDS_CLARIFICATION":
      return "warning";
    case "EXCLUDED":
      return "unknown";
    default:
      return "unknown";
  }
}

/**
 * Sur quoi repose une suggestion.
 *
 * Une base déterministe et une proposition du modèle ne portent pas la même
 * couleur : c'est le seul indice qu'un lecteur pressé regarde avant de cliquer
 * « Ajouter ».
 */
export function suggestionBasisTone(basis: string): BadgeTone {
  switch (basis) {
    case "RELATION":
      return "available";
    case "HISTORY":
      return "info";
    case "DOCUMENT":
      return "reserved";
    case "MODEL_KNOWLEDGE":
      return "warning";
    default:
      return "unknown";
  }
}

/** Un manque net n'est pas un ordre d'achat : il alerte, il ne commande pas. */
export function shortageTone(shortage: string): BadgeTone {
  return Number(shortage) > 0 ? "warning" : "available";
}

/** Le mode d'une politique. « Auto » se remarque : c'est le seul qui agit seul. */
export function policyModeTone(mode: string): BadgeTone {
  switch (mode) {
    case "AUTO":
      return "warning";
    case "AUTO_WITH_APPROVAL":
      return "info";
    case "MANUAL":
      return "unknown";
    default:
      return "unknown";
  }
}

/** Où en est une proposition de seuil. */
export function recommendationTone(status: string): BadgeTone {
  switch (status) {
    case "PENDING":
      return "warning";
    case "APPROVED":
      return "info";
    case "APPLIED":
      return "available";
    case "REJECTED":
      return "out";
    case "SUPERSEDED":
      return "unknown";
    default:
      return "unknown";
  }
}

/**
 * La couleur d'un statut de demande de prix (V1.6-04).
 *
 * `READY_TO_SEND` est « info » et pas « available » : figée n'est pas envoyée,
 * et un vert de réussite raconterait qu'une étape est terminée alors que la
 * demande attend encore d'être transmise à la main.
 */
export function rfqTone(status: string): BadgeTone {
  switch (status) {
    case "READY_TO_SEND":
      return "info";
    case "DRAFT":
      return "unknown";
    case "CANCELLED":
      return "out";
    default:
      return "unknown";
  }
}

/**
 * La couleur d'un message sortant (V1.6-05).
 *
 * `UNKNOWN_DELIVERY` est en « warning » et non en « out » : ce n'est pas un
 * échec, c'est une incertitude, et la peindre comme un échec inviterait à
 * renvoyer.
 */
export function outboundTone(status: string): BadgeTone {
  switch (status) {
    case "SENT":
      return "available";
    case "SENDING":
    case "QUEUED":
      return "info";
    case "UNKNOWN_DELIVERY":
      return "warning";
    case "FAILED":
      return "out";
    case "CANCELLED":
      return "unknown";
    default:
      return "unknown";
  }
}

/**
 * La couleur d'un état de transmission — demande de prix **ou** bon de
 * commande (V1.6-14.3).
 *
 * Une seule fonction pour les deux énumérations parce que la correspondance
 * est la même : `NOT_SENT` et `NOT_QUEUED` tombent tous deux sur le défaut.
 * En écrire une seconde ferait deux vérités à tenir en phase, et le jour où
 * elles divergeraient un envoi incertain se peindrait comme un échec.
 *
 * `UNKNOWN_DELIVERY` est « warning » et non « out » : ce n'est pas un échec,
 * c'est une incertitude, et les confondre ferait croire qu'on peut renvoyer.
 */
/**
 * La couleur d'un etat de reponse fournisseur (V1.6-14.6).
 *
 * Une fonction distincte de `dispatchTone` bien que les tons se ressemblent :
 * les deux vocabulaires n'ont aucune valeur commune, et les fusionner ferait
 * qu'ajouter un etat d'un cote changerait la couleur de l'autre.
 *
 * `NOT_ACKNOWLEDGED` est neutre, pas inquietant : un fournisseur n'a aucune
 * obligation de repondre au courriel, et le peindre en rouge ferait courir
 * apres une reponse qui n'est pas due.
 */
export function acknowledgementTone(state: string): BadgeTone {
  switch (state) {
    case "CONFIRMED":
      return "available";
    case "CHANGES_REQUESTED":
      return "warning";
    case "DECLINED":
      return "out";
    default:
      return "unknown";
  }
}

export function dispatchTone(state: string): BadgeTone {
  switch (state) {
    case "SENT":
      return "available";
    case "QUEUED":
    case "SENDING":
      return "info";
    case "UNKNOWN_DELIVERY":
      return "warning";
    case "FAILED":
      return "out";
    default:
      return "unknown";
  }
}
